import React, { useState, useEffect } from 'react';
import staffApi from '../../staffApi';

export default function ManageLinksModal({ test, onClose }) {
  const [tutorEmail, setTutorEmail] = useState('');
  const [tutorName, setTutorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [creatingLink, setCreatingLink] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(new Set());

  useEffect(() => {
    loadLinks();
  }, [test.id]);

  const loadLinks = async () => {
    try {
      setLoadingLinks(true);
      const data = await staffApi.getTestLinks(test.id);
      setLinks(data);
    } catch (error) {
      console.error('Error loading links:', error);
      alert('Failed to load test links');
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    if (!tutorEmail.trim()) {
      alert('Please enter tutor email');
      return;
    }

    try {
      setCreatingLink(true);
      await staffApi.createTestLink(test.id, tutorEmail, tutorName);
      setTutorEmail('');
      setTutorName('');
      await loadLinks();
      alert('Test link created successfully!');
    } catch (error) {
      console.error('Error creating link:', error);
      alert('Failed to create test link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleSendEmail = async (linkId) => {
    if (!window.confirm('Send test link email to this candidate?')) {
      return;
    }

    try {
      setSendingEmails(prev => new Set(prev).add(linkId));
      await staffApi.sendTestLinkEmail(linkId);
      await loadLinks();
      alert('Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email');
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
    }
  };

  const handleCopyLink = (linkUrl) => {
    navigator.clipboard.writeText(linkUrl).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link');
    });
  };

  const getStatusBadge = (link) => {
    if (link.is_used) {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">Used</span>;
    }
    if (link.is_expired) {
      return <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">Expired</span>;
    }
    if (link.sent_at) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">Sent</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-semibold">Not Sent</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="text-3xl">🔗</span>
              Manage Test Links
            </h2>
            <p className="text-gray-600">{test.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Create New Link Form */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-5 mb-6 border border-indigo-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>➕</span>
            Create New Test Link
          </h3>
          <form onSubmit={handleCreateLink}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tutor Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={tutorEmail}
                  onChange={(e) => setTutorEmail(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                  placeholder="tutor@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tutor Name (Optional)
                </label>
                <input
                  type="text"
                  value={tutorName}
                  onChange={(e) => setTutorName(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                  placeholder="John Doe"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={creatingLink}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {creatingLink ? 'Creating...' : '➕ Create Link'}
            </button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              You can create multiple links for the same email address
            </p>
          </form>
        </div>

        {/* Links List */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📋</span>
            All Test Links ({links.length})
          </h3>

          {loadingLinks ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Loading links...</p>
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No test links created yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-all"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="font-semibold text-gray-900">
                          {link.tutor_name || link.tutor_email}
                        </div>
                        {getStatusBadge(link)}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Email:</strong> {link.tutor_email}</p>
                        <p><strong>Created:</strong> {formatDate(link.created_at)}</p>
                        <p><strong>Expires:</strong> {formatDate(link.expires_at)}</p>
                        {link.sent_at && <p><strong>Sent:</strong> {formatDate(link.sent_at)}</p>}
                        {link.used_at && <p><strong>Used:</strong> {formatDate(link.used_at)}</p>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      {!link.sent_at && (
                        <button
                          onClick={() => handleSendEmail(link.id)}
                          disabled={sendingEmails.has(link.id)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-all disabled:opacity-50"
                        >
                          {sendingEmails.has(link.id) ? 'Sending...' : '📧 Send Email'}
                        </button>
                      )}
                      <button
                        onClick={() => handleCopyLink(link.test_url)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all"
                      >
                        📋 Copy Link
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Test Link URL:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={link.test_url}
                        readOnly
                        className="flex-1 text-xs bg-gray-50 border border-gray-300 rounded px-2 py-1 text-gray-700"
                      />
                      <button
                        onClick={() => handleCopyLink(link.test_url)}
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

